import { FunctionComponent, useEffect, useState } from 'react';
import { Headline, Div, BodyText, Icon, Title } from '@gaiads/telia-react-component-library';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  COUNTRIES,
  OrganizationAssetCard,
  OrganizationAssetCardTranslations,
  createTicket,
  AssetAssignToUser,
  AssetAssignToUserTranslations,
  AssetAssignToOrganization,
  UserDetails,
  EolFlowFinishFromChoice,
  EndOfLeaseChoiceTranslations,
  AssetAssignToOrganizationTranslations,
} from '@cdaas/reusable-components-daas';
import {
  AssetEndOfLeaseRequest,
  InvoicingAccountOptionResponseContents,
  InvoicingAccountResponse,
  OrganizationAssetResponse,
  UserAssetResponse,
  UserRole,
  UserWithOrganizationAndUserGroupResponse,
} from '@cdaas/api-clients/dist/asset-user-openapi';

import {
  assignAssetToOrganization,
  assignAssetToUser,
  confirmOrCancelEndOfLeaseDecision,
  getAsset as getAssetApi,
  getAUser,
  getInvoicingAccounts,
  getTicketURI,
  getUsersAllAssets as getUsersAllAssetsApi,
  getUsersFromOrganizationOrUserGroup,
  hasUserARequiredRole,
  isWebshopEnabled,
  postEndOfLeaseDecision,
  renderContactSupportText,
  updateOrganizationAsset,
} from '../../utils';
import { refreshUserInfo, shopSetOrderForOrganization, shopSetOrderOnBehalfUser, showGrowlNotification } from '../../store/actions';
import { RootState } from '../../store/reducers';
import { User } from '../../types';
import CustomLoader from '../../Components/CustomLoader/CustomLoader';

const OrganizationAssetDetails: FunctionComponent = () => {
  const [asset, setAsset] = useState<OrganizationAssetResponse>();
  const [assetUser, setAssetUser] = useState<UserWithOrganizationAndUserGroupResponse>();
  const [userAssets, setUserAssets] = useState<UserAssetResponse[]>([]);

  const loggedInUser = useSelector((state: RootState) => state.authentication.user);
  const organization = useSelector((state: RootState) => state.authentication.user?.organization);
  const selectedOrganization = useSelector((state: RootState) => state.authentication.selectedOrganization);
  const useBuyoutWithPriceFlow = useSelector((state: RootState) => state.featureFlags.FEATURE_FLAG_FRONTEND_FLOW_BUYOUT_WITH_PRICE_ENABLED);
  const recurringMonthlyExtensionNoEnabled = useSelector(
    (state: RootState) => state.featureFlags.FEATURE_FLAG_RECURRING_MONTHLY_EXTENSION_NO_ENABLED,
  );

  const [assignToDifferentUserModalOpen, setAssignToDifferentUserModalOpen] = useState(false);
  const [assignToOrganizationModalOpen, setAssignToOrganizationModalOpen] = useState(false);
  const [organizationUsers, setOrganizationUsers] = useState<User[]>([]);
  const [organizationUsersLoading, setOrganizationUsersLoading] = useState(false);
  const [invoicingAccountsLoading, setInvoicingAccountsLoading] = useState(true);
  const [invoicingAccounts, setInvoicingAccounts] = useState<InvoicingAccountOptionResponseContents[]>([]);
  const [assetAssignLoading, setAssetAssignLoading] = useState(false);

  const [loading, setLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const assetId = useParams<{ id: string }>().id;
  const history = useHistory();
  const { t } = useTranslation('assets');
  const { t: tGlobal } = useTranslation('globalComponents');
  const { t: tShop } = useTranslation('shop');
  const { t: tErrors } = useTranslation('apiErrors');

  const automaticExtension: boolean = asset?.prices?.lease?.can_lease_be_auto_extended ?? false;

  const contractEndsTitleMessage = t(
    recurringMonthlyExtensionNoEnabled && automaticExtension
      ? 'AssetCard.endOfLeaseNotifications.contractEndsTitleUpgrade'
      : 'AssetCard.endOfLeaseNotifications.contractEndsTitle',
  );

  const decisionNotYetDoneMessage = t(
    automaticExtension
      ? recurringMonthlyExtensionNoEnabled
        ? 'AssetCard.endOfLeaseNotifications.decisionNotYetDoneMessageExtensionRecurringAdmin'
        : 'AssetCard.endOfLeaseNotifications.decisionNotYetDoneMessageExtensionAdmin'
      : 'AssetCard.endOfLeaseNotifications.decisionNotYetDoneMessageLeaseEnd',
  );

  const daysLeftForDecisionMessage = t(
    recurringMonthlyExtensionNoEnabled
      ? automaticExtension
        ? 'AssetCard.endOfLeaseNotifications.daysLeftForDecisionRecurring'
        : 'AssetCard.endOfLeaseNotifications.daysLeftForDecisionLast'
      : 'AssetCard.endOfLeaseNotifications.daysLeftForDecision',
  );

  const translations: OrganizationAssetCardTranslations = {
    validUntil: t('AssetCard.validUntil'),
    monthShort: t('AssetCard.monthShort', { count: 1 }),
    monthLeft: t('AssetCard.monthLeft'),
    monthsLeft: t('AssetCard.monthsLeft'),
    vatInfo: t('AssetCard.vatIncluded'),
    replacedDeviceTag: t('AssetCard.replacedDeviceTag'),
    contractEndedDate: t('AssetCard.contractEndedDate'),
    deviceDetails: t('AssetCard.deviceDetails'),
    deviceBought: t('AssetCard.eolMadeDecision.deviceBought'),
    deviceAwaitingReturn: t('AssetCard.returnStatus.deviceAwaitingReturn'),
    deviceReturnInTransit: t('AssetCard.returnStatus.deviceReturnInTransit'),
    deviceReturnReceived: t('AssetCard.returnStatus.deviceReturnReceived'),
    deviceReturnedApproved: t('AssetCard.returnStatus.deviceReturnedApproved'),
    deviceReturnedWithCloudLock: t('AssetCard.returnStatus.deviceReturnedWithCloudLock'),
    deviceReturnedWithConditions: t('AssetCard.returnStatus.deviceReturnedWithConditions'),
    incorrectDeviceReturned: t('AssetCard.returnStatus.incorrectDeviceReturned'),
    deviceReturned: t('AssetCard.returnStatus.deviceReturned'),
    deviceNotReturnedInvoiced: t('AssetCard.returnStatus.deviceNotReturnedInvoiced'),
    otherReturnStatus: t('AssetCard.returnStatus.otherReturnStatus'),
    deviceNotReturned: t('AssetCard.eolMadeDecision.deviceNotReturned'),
    location: t('AssetCard.location'),
    locationReference: t('AssetCard.locationReference'),
    invoicingAccount: t('AssetCard.invoicingAccount'),
    activeInvoicingAccount: t('AssetCard.activeInvoicingAccount'),
    user: t('AssetCard.user'),
    userName: t('AssetCard.userName'),
    phoneNumber: t('AssetCard.phoneNumber'),
    otherDevices: t('AssetCard.otherDevices'),
    events: t('AssetCard.events'),
    tickets: t('AssetCard.tickets'),
    createTicketButton: t('AssetCard.createTicketButton'),
    createSecondTicketButton: t('AssetCard.createSecondTicketButton'),
    secondTicketModal: {
      title: t('AssetCard.secondTicketModal.title'),
      button: t('AssetCard.secondTicketModal.button'),
    },
    imeiNumber: t('AssetCard.imeiNumber'),
    previousImeis: t('AssetCard.previousImeis'),
    sku: t('AssetCard.sku'),
    bundleAccessories: t('AssetCard.bundleAccessories'),
    bundleOrderId: t('AssetCard.bundleOrderId'),
    bundleServices: t('AssetCard.bundleServices'),
    insurance: t('AssetCard.insurance'),
    ticketStatus: t('AssetCard.ticketStatus'),
    ticketOpen: t('AssetCard.ticketOpen'),
    ticketClosed: t('AssetCard.ticketClosed'),
    link: t('AssetCard.link'),
    contractEnded: t('AssetCard.contractEnded'),
    contractExtended: t('AssetCard.contractExtended'),
    endOfLeaseSelected: t('AssetCard.endOfLeaseSelected'),
    returnEnvelopeSent: t('AssetCard.returnEnvelopeSent'),
    endOfLeaseReturnColtHardwareDeliveryId: t('AssetCard.endOfLeaseReturnColtHardwareDeliveryId'),
    months: t('AssetCard.months'),
    delivered: t('AssetCard.delivered'),
    shipped: t('AssetCard.shipped'),
    endOfLeaseNotificationTranslations: {
      contractEndsTitle: contractEndsTitleMessage,
      decisionNotYetDoneMessage: decisionNotYetDoneMessage,
      daysLeftForDecision: daysLeftForDecisionMessage,
      buyoutTitle: t('AssetCard.endOfLeaseNotifications.buyoutTitleAdmin'),
      buyoutMessage: t('AssetCard.endOfLeaseNotifications.buyoutMessageAdmin'),
      returnTitle: t('AssetCard.endOfLeaseNotifications.returnTitleAdmin'),
      returnMessage: t('AssetCard.endOfLeaseNotifications.returnMessageAdmin'),
    },
    assignToDifferentUser: t('AssetCard.assignToDifferentUser'),
    assignToOrganization: t('AssetCard.assignToOrganization'),
    companyDevice: t('companyDevice'),
    inactiveUser: t('inactiveUser'),
    inactiveAssetInvoicingAccount: t('AssetCard.inactiveAssetInvoicingAccount'),
    eolSelectedBuyout: t('AssetCard.eolActionEventMessage.buyoutSelectedEvent'),
    eolSelectedReturn: t('AssetCard.eolActionEventMessage.returnDeviceEvent'),
    eolSelectedNotReturned: t('AssetCard.eolActionEventMessage.notReturnedEvent'),
    invoicingAccountSelection: {
      dropdownLabel: t('AssetCard.invoicingAccountSelection.dropdownLabel'),
      clearSelection: t('AssetCard.invoicingAccountSelection.clearSelection'),
      noOptionsFoundInfo: t('AssetCard.invoicingAccountSelection.noOptionsFoundInfo'),
      helperText: t('AssetCard.invoicingAccountSelection.helperText'),
      errorMessage: t('AssetCard.invoicingAccountSelection.errorMessage'),
      accountNotValidTitle: t('AssetCard.invoicingAccountSelection.accountNotValidTitle'),
      accountNotValidMessage: t('AssetCard.invoicingAccountSelection.accountNotValidMessage'),
    },
  };

  const assignToUserTranslations: AssetAssignToUserTranslations = {
    deviceToBeAssigned: {
      name: t('AssetCard.assign.deviceToBeAssigned.name'),
      price: t('AssetCard.assign.deviceToBeAssigned.price'),
      currentUser: t('AssetCard.assign.deviceToBeAssigned.currentUser'),
      currentInvoicingAccount: t('AssetCard.assign.deviceToBeAssigned.currentInvoicingAccount'),
    },
    selectUserInput: {
      label: t('AssetCard.assign.selectUserInput.label'),
      placeholder: t('AssetCard.assign.selectUserInput.placeholder'),
    },
    userToAssignDeviceTo: {
      nameAndGroup: t('AssetCard.assign.userToAssignDeviceTo.nameAndGroup'),
      contact: t('AssetCard.assign.userToAssignDeviceTo.contact'),
      deviceLimit: t('AssetCard.assign.userToAssignDeviceTo.deviceLimit'),
      maxPricePaidByEmployer: t('AssetCard.assign.userToAssignDeviceTo.maxPricePaidByEmployer'),
    },
    newInvoicingAccount: {
      label: t('AssetCard.assign.newInvoicingAccount.label'),
      dropdownLabel: t('AssetCard.assign.newInvoicingAccount.dropdownLabelUser'),
      clearSelection: t('AssetCard.assign.newInvoicingAccount.clearSelection'),
      noOptionsFoundInfo: t('AssetCard.assign.newInvoicingAccount.noOptionsFoundInfo'),
      helperText: t('AssetCard.assign.newInvoicingAccount.helperTextUser'),
    },
    vatInfo: t('AssetCard.assign.vatIncluded'),
    month: t('AssetCard.assign.month'),
    months: t('AssetCard.assign.months'),
    unlimited: t('AssetCard.assign.unlimited'),
    leaseContinueInfo: {
      part1: t('AssetCard.assign.leaseContinueInfo.part1'),
      part2: t('AssetCard.assign.leaseContinueInfo.part2'),
    },
    modalHeading: t('AssetCard.assign.modalHeadingUser'),
    saveButton: t('AssetCard.assign.saveButton'),
    cancelButton: t('AssetCard.assign.cancelButton'),
    errors: {
      userDetailLoadingApiError: t('AssetCard.assign.errors.userDetailLoadingApiError'),
      cantBeAssignedBecauseOfSingleDevicePolicyError: t('AssetCard.assign.errors.cantBeAssignedBecauseOfSingleDevicePolicyError'),
      assignApiError: t('AssetCard.assign.errors.assignApiErrorUser'),
    },
    confirmationModal: {
      title: t('AssetCard.assign.confirmationModal.titleUser'),
      confirmInfo: t('AssetCard.assign.confirmationModal.confirmInfoUser'),
      confirmList: {
        device: t('AssetCard.assign.confirmationModal.confirmList.device'),
        price: t('AssetCard.assign.confirmationModal.confirmList.price'),
        remainingLeasePeriod: {
          remainingLeasePeriod: t('AssetCard.assign.confirmationModal.confirmList.remainingLeasePeriod.remainingLeasePeriod'),
          outOf: t('AssetCard.assign.confirmationModal.confirmList.remainingLeasePeriod.outOf'),
        },
        allowance: {
          allowance: t('AssetCard.assign.confirmationModal.confirmList.allowance.allowance'),
          possibleSalaryDeduction: t('AssetCard.assign.confirmationModal.confirmList.allowance.possibleSalaryDeduction'),
        },
        deviceLimit: t('AssetCard.assign.confirmationModal.confirmList.deviceLimit'),
      },
      vatInfo: t('AssetCard.assign.confirmationModal.vatIncluded'),
      month: t('AssetCard.assign.confirmationModal.month'),
      months: t('AssetCard.assign.confirmationModal.months'),
      confirmButton: t('AssetCard.assign.confirmationModal.confirmButton'),
      cancelButton: t('AssetCard.assign.confirmationModal.cancelButton'),
    },
    companyDevice: t('companyDevice'),
    inactiveUser: t('inactiveUser'),
  };

  const assignToOrganizationTranslations: AssetAssignToOrganizationTranslations = {
    deviceToBeAssigned: {
      name: t('AssetCard.assign.deviceToBeAssigned.name'),
      price: t('AssetCard.assign.deviceToBeAssigned.price'),
      currentUser: t('AssetCard.assign.deviceToBeAssigned.currentUser'),
      currentInvoicingAccount: t('AssetCard.assign.deviceToBeAssigned.currentInvoicingAccount'),
    },
    newInvoicingAccount: {
      label: t('AssetCard.assign.newInvoicingAccount.label'),
      dropdownLabel: t('AssetCard.assign.newInvoicingAccount.dropdownLabelOrganization'),
      clearSelection: t('AssetCard.assign.newInvoicingAccount.clearSelection'),
      noOptionsFoundInfo: t('AssetCard.assign.newInvoicingAccount.noOptionsFoundInfo'),
      helperText: t('AssetCard.assign.newInvoicingAccount.helperTextOrganization'),
    },
    vatInfo: t('AssetCard.assign.vatIncluded'),
    month: t('AssetCard.assign.month'),
    months: t('AssetCard.assign.months'),
    unlimited: t('AssetCard.assign.unlimited'),
    leaseContinueInfo: {
      part1: t('AssetCard.assign.leaseContinueInfo.part1'),
      part2: t('AssetCard.assign.leaseContinueInfo.part2'),
    },
    modalHeading: t('AssetCard.assign.modalHeadingOrganization'),
    saveButton: t('AssetCard.assign.saveButton'),
    cancelButton: t('AssetCard.assign.cancelButton'),
    errors: {
      assignApiError: t('AssetCard.assign.errors.assignApiErrorOrganization'),
    },
    confirmationModal: {
      title: t('AssetCard.assign.confirmationModal.titleOrganization'),
      confirmInfo: t('AssetCard.assign.confirmationModal.confirmInfoOrganization'),
      confirmList: {
        device: t('AssetCard.assign.confirmationModal.confirmList.device'),
        price: t('AssetCard.assign.confirmationModal.confirmList.price'),
        remainingLeasePeriod: {
          remainingLeasePeriod: t('AssetCard.assign.confirmationModal.confirmList.remainingLeasePeriod.remainingLeasePeriod'),
          outOf: t('AssetCard.assign.confirmationModal.confirmList.remainingLeasePeriod.outOf'),
        },
      },
      vatInfo: t('AssetCard.assign.confirmationModal.vatIncluded'),
      month: t('AssetCard.assign.confirmationModal.month'),
      months: t('AssetCard.assign.confirmationModal.months'),
      confirmButton: t('AssetCard.assign.confirmationModal.confirmButton'),
      cancelButton: t('AssetCard.assign.confirmationModal.cancelButton'),
    },
    companyDevice: t('companyDevice'),
    inactiveUser: t('inactiveUser'),
  };

  const endOfLeaseChoiceTranslations: EndOfLeaseChoiceTranslations = {
    buyoutDeviceOptionTitle: t('AssetCard.assetActions.purchaseDeviceOption'),
    buyoutDeviceOptionText: t('AssetCard.assetActions.purchaseDeviceText'),
    buyoutDeviceOptionWithoutPriceButton: t('AssetCard.assetActions.purchaseDeviceButton'),
    buyoutDeviceOptionWithPricePurchaseButton: t('AssetCard.assetActions.purchaseButton'),
    buyoutDeviceOptionWithPriceAskForPriceButton: t('AssetCard.assetActions.askForPriceButton'),
    buyoutDeviceOptionWithPricePurchaseAnywayButton: t('AssetCard.assetActions.purchaseAnywayButton'),
    buyoutDeviceOptionWithPriceName: t('AssetCard.assetActions.purchaseDevicePriceName'),
    buyoutDeviceOptionWithPriceIncludingVat: t('AssetCard.assetActions.purchaseDevicePriceIncludingVat'),
    buyoutDeviceOptionWithPriceExcludingVat: t('AssetCard.assetActions.purchaseDevicePriceExcludingVat'),
    buyoutDeviceOptionWithPriceWaitingForPrice: t('AssetCard.assetActions.waitingForPrice'),
    buyoutDeviceWithPriceWaitingPriceNotificationTitle: t('AssetCard.endOfLeaseNotifications.waitingForBuyoutPriceTitle'),
    buyoutDeviceWithPriceWaitingPriceNotificationText: t('AssetCard.endOfLeaseNotifications.waitingForBuyoutPriceMessageAdmin'),
    buyoutDeviceWithPriceEolNotificationContractEndsTitle: t('AssetCard.endOfLeaseNotifications.contractEndsTitle'),
    buyoutDeviceWithPriceEolNotificationDecisionNotYetDoneMessage: decisionNotYetDoneMessage,
    buyoutDeviceWithPriceEolNotificationDaysLeftForDecision: t('AssetCard.endOfLeaseNotifications.daysLeftForDecision'),
    buyoutDeviceWithPriceEolNotificationBuyoutTitle: t('AssetCard.endOfLeaseNotifications.buyoutTitleAdmin'),
    buyoutDeviceWithPriceEolNotificationBuyoutMessage: t('AssetCard.endOfLeaseNotifications.buyoutMessageAdmin'),
    buyoutDeviceWithPriceEolNotificationReturnTitle: t('AssetCard.endOfLeaseNotifications.returnTitleAdmin'),
    buyoutDeviceWithPriceEolNotificationReturnMessage: t('AssetCard.endOfLeaseNotifications.returnMessageAdmin'),
    buyoutDeviceModalPurchaseAnywayTitle: t('AssetCard.purchaseWithPriceDeviceModals.purchaseAssetAnywayModal.title'),
    buyoutDeviceModalPurchaseAnywayText1: t('AssetCard.purchaseWithPriceDeviceModals.purchaseAssetAnywayModal.content.paragraph1'),
    buyoutDeviceModalPurchaseAnywayText2: t('AssetCard.purchaseWithPriceDeviceModals.purchaseAssetAnywayModal.content.paragraph2'),
    buyoutDeviceModalPurchaseAnywayText3: t('AssetCard.purchaseWithPriceDeviceModals.purchaseAssetAnywayModal.content.paragraph3'),
    buyoutDeviceModalPurchaseAnywayConfirmButton: t('AssetCard.purchaseConfirmButton'),
    buyoutDeviceModalPurchaseAnywayCancelButton: t('AssetCard.cancelButton'),
    buyoutDevicePurchaseConfirmationCheckboxLabel: t('AssetCard.purchaseConfirmationCheckboxLabel'),
    buyoutDeviceModalNoPriceAvailableTitle: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.title'),
    buyoutDeviceModalNoPriceAvailableDescription: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.description'),
    buyoutDeviceModalNoPriceAvailableOptionsTitle: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.optionsTitle'),
    buyoutDeviceModalNoPriceAvailableOptionWait: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.optionWaitAdmin'),
    buyoutDeviceModalNoPriceAvailableOptionPurchaseAnyway: t(
      'AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.optionPurchaseAnyway',
    ),
    buyoutDeviceModalNoPriceAvailableOptionCancel: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.optionCancel'),
    buyoutDeviceModalNoPriceAvailableCancelButton: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.cancelButton'),
    buyoutDeviceModalNoPriceAvailableWaitButton: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.waitButton'),
    buyoutDeviceModalNoPriceAvailablePurchaseButton: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.purchaseButton'),
    buyoutDeviceModalPriceAvailableTitle: t('AssetCard.purchaseWithPriceDeviceModals.priceAvailableModal.title'),
    buyoutDeviceModalPriceAvailableText1: t('AssetCard.purchaseWithPriceDeviceModals.priceAvailableModal.generalInfo.paragraph1'),
    buyoutDeviceModalPriceAvailableText2: t('AssetCard.purchaseWithPriceDeviceModals.priceAvailableModal.generalInfo.paragraph2'),
    buyoutDeviceModalPriceAvailableCancelButton: t('AssetCard.cancelButton'),
    buyoutDeviceModalPriceAvailableConfirmButton: t('AssetCard.purchaseConfirmButton'),
    buyoutDeviceModalWithoutPriceTitle: t('AssetCard.purchaseWithoutPriceDeviceModals.title'),
    buyoutDeviceModalWithoutPriceText1: t('AssetCard.purchaseWithoutPriceDeviceModals.priceNotAvailable.part1'),
    buyoutDeviceModalWithoutPriceText2_1: t('AssetCard.purchaseWithoutPriceDeviceModals.priceNotAvailable.part2Reusable_1'),
    buyoutDeviceModalWithoutPriceText2_2: t('AssetCard.purchaseWithoutPriceDeviceModals.priceNotAvailable.part2Reusable_2'),
    buyoutDeviceModalWithoutPriceConfirmButton: t('AssetCard.purchaseConfirmButton'),
    buyoutDeviceModalWithoutPriceCancelButton: t('AssetCard.cancelButton'),
    buyoutDeviceModalWithoutPriceConfirmedTitle: t('AssetCard.purchaseWithoutPriceDeviceModals.confirmedTitle'),
    buyoutDeviceModalWithoutPriceConfirmedText: t('AssetCard.purchaseWithoutPriceDeviceModals.confirmedText'),
    buyoutDeviceModalWithoutPriceConfirmedButton: t('AssetCard.okButton'),
    upgradeDeviceOptionText: t('AssetCard.assetActions.upgradeDeviceTextAdmin'),
    upgradeDeviceModalText1: t('AssetCard.upgradeDeviceModal.text1'),
    upgradeDeviceModalText2: t('AssetCard.upgradeDeviceModal.text2'),
    upgradeDeviceModalText3: t('AssetCard.upgradeDeviceModal.text3'),
    extendDeviceOptionTitle: t('AssetCard.assetActions.extendDeviceOption'),
    extendDeviceOptionTextStart: t('AssetCard.assetActions.extendDeviceTextReusableStart'),
    extendDeviceOptionTextEnd: t('AssetCard.assetActions.extendDeviceTextReusableEnd'),
    extendDeviceOptionButton: t('AssetCard.assetActions.extendDeviceButton'),
    extendDeviceModalsTitle: t('AssetCard.extendDeviceModals.title'),
    extendDeviceModalsText: t('AssetCard.extendDeviceModals.text'),
    extendDeviceModalsOptionTitle: t('AssetCard.extendDeviceModals.optionTitle'),
    extendDeviceModalsMonths: t('AssetCard.months'),
    extendDeviceModalsConfirmSelectionTitle: t('AssetCard.extendDeviceModals.confirmSelectionTitle'),
    extendDeviceModalsConfirmSelectionText: t('AssetCard.extendDeviceModals.confirmSelectionText'),
    extendDeviceModalsConfirmedTitle: t('AssetCard.extendDeviceModals.confirmedTitle'),
    extendDeviceModalsNextButton: t('AssetCard.nextButton'),
    extendDeviceModalsConfirmButton: t('AssetCard.confirmButton'),
    extendDeviceModalsCancelButton: t('AssetCard.cancelButton'),
    extendDeviceModalsOkButton: t('AssetCard.okButton'),
    extendDeviceOrSeparator: t('AssetCard.extendDeviceModals.orSeparator'),
    returnDeviceOptionTitle: t('AssetCard.assetActions.returnDeviceOption'),
    returnDeviceOptionText: t('AssetCard.assetActions.returnDeviceText'),
    returnDeviceOptionButton: t('AssetCard.assetActions.returnDeviceButton'),
    returnDeviceModalTitle: t('AssetCard.returnDeviceModal.title'),
    returnDeviceModalContinueButton: t('AssetCard.continueButton'),
    returnDeviceModalCancelButton: t('AssetCard.cancelButton'),
    returnDeviceModalText1: t('AssetCard.returnDeviceModal.text1'),
    returnDeviceModalText2: t('AssetCard.returnDeviceModal.text2'),
    returnDeviceModal: {
      nextSteps: t('AssetCard.returnDeviceModal.nextSteps'),
      step1: t('AssetCard.returnDeviceModal.step1'),
      step2: t('AssetCard.returnDeviceModal.step2'),
      step2Detail: t('AssetCard.returnDeviceModal.step2Detail'),
      step3: t('AssetCard.returnDeviceModal.step3'),
      step3Detail: t('AssetCard.returnDeviceModal.step3Detail'),
      step4: t('AssetCard.returnDeviceModal.step4'),
      step4Detail: t('AssetCard.returnDeviceModal.step4Detail'),
    },
    returnDeviceReturnConfirmedHeadline: t('ReturnAsset.returnConfirmed.headline'),
    returnDeviceReturnConfirmedUpgradeTitle: t('ReturnAsset.returnConfirmed.upgradeTitle'),
    returnDeviceReturnConfirmedText1: t('ReturnAsset.returnConfirmed.text1'),
    returnDeviceReturnConfirmedText2: t('ReturnAsset.returnConfirmed.text2'),
    returnDeviceReturnConfirmedText3: t('ReturnAsset.returnConfirmed.text3'),
    returnDeviceReturnConfirmedNextButton: t('ReturnAsset.returnConfirmed.nextButton'),
    returnDeviceReturnConfirmedUpgradeButton: t('ReturnAsset.returnConfirmed.upgradeNextButton'),
    returnDeviceOrderReturn: {
      headline: t('ReturnAsset.orderReturnEnvelope.headline'),
      checkoutDetails: {
        employeeTitle: tShop('checkout.detailsPage.employeeTitle'),
        deliveryTitle: t('ReturnAsset.orderReturnEnvelope.deliveryTitle'),
        deliveryText: t('ReturnAsset.orderReturnEnvelope.deliveryTextAdmin'),
        recipientText: t('ReturnAsset.orderReturnEnvelope.recipientText'),
        contactText: t('ReturnAsset.orderReturnEnvelope.contactText'),
        deliveryQuestion: t('ReturnAsset.orderReturnEnvelope.deliveryQuestion'),
        companyName: t('ReturnAsset.orderReturnEnvelope.companyName'),
        firstName: tShop('checkout.detailsPage.form.firstName'),
        lastName: tShop('checkout.detailsPage.form.lastName'),
        email: tShop('checkout.detailsPage.form.email'),
        phoneNbr: tShop('checkout.detailsPage.form.phoneNbr'),
        streetAddress: t('ReturnAsset.orderReturnEnvelope.streetAddress'),
        city: tShop('checkout.detailsPage.form.city'),
        postalCode: tShop('checkout.detailsPage.form.postalCode'),
        country: tShop('checkout.detailsPage.form.country'),
        countryPlaceholder: tShop('checkout.detailsPage.form.countryPlaceholder'),
        homeDeliveryOption: tShop('checkout.detailsPage.form.homeDeliveryOption'),
        homeDeliveryOptionDetails: t('ReturnAsset.orderReturnEnvelope.homeDeliveryOptionDetails'),
        companyDeliveryOption: tShop('checkout.detailsPage.form.companyDeliveryOption'),
        companyDeliveryOptionDetails: t('ReturnAsset.orderReturnEnvelope.companyDeliveryOptionDetails'),
        required: tShop('checkout.detailsPage.form.rules.required'),
        invalidEmail: tShop('checkout.detailsPage.form.rules.invalidEmail'),
        invalidPhoneNumber: tShop('checkout.detailsPage.form.rules.invalidPhoneNumber'),
        invalidPostalCode: tShop('checkout.detailsPage.form.rules.invalidPostalCode'),
        nextButton: t('ReturnAsset.orderReturnEnvelope.confirmButton'),
        cancelButton: t('ReturnAsset.orderReturnEnvelope.cancelButton'),
        Finland: tGlobal('countries.Finland'),
        Sweden: tGlobal('countries.Sweden'),
        Norway: tGlobal('countries.Norway'),
        Denmark: tGlobal('countries.Denmark'),
        UnitedKingdom: tGlobal('countries.UnitedKingdom'),
      },
      returnableDevice: {
        title: t('ReturnAsset.returnReturnableDevice.title'),
        text1: t('ReturnAsset.returnReturnableDevice.text1Admin'),
        text2: t('ReturnAsset.returnReturnableDevice.text2Admin'),
      },
    },
  };

  useEffect(() => {
    if (assetUser) {
      getUsersAllAssetsApi(assetUser.id).then(assets => {
        if (assets) {
          setUserAssets(assets);
        }
      });
    } else {
      setUserAssets([]);
    }
  }, [assetUser]);

  const getUser = async (userId: string) => {
    const user = await getAUser(userId);
    if (user) setAssetUser(user);
  };

  const getOrganizationAsset = async (assetId: string): Promise<void> => {
    if (!asset) {
      setLoading(true);
    }
    const response = await getAssetApi(assetId);
    if (response) {
      setAsset(response);
      // Get user for end of lease choice
      if (response.end_of_lease_options && response.end_user.user?.id) {
        getUser(response.end_user.user.id);
      }
    } else {
      dispatch(showGrowlNotification(t('getOrganizationDevicesError'), 'error'));
      setAsset(undefined);
    }
    setLoading(false);
  };

  const getOrganizationInvoicingAccounts = async () => {
    setInvoicingAccountsLoading(true);
    const res = await getInvoicingAccounts();
    if (res) {
      setInvoicingAccounts(res);
    } else {
      dispatch(showGrowlNotification(tErrors('invoicingAccount.getAllInvoicingAccountsError'), 'error'));
    }
    setInvoicingAccountsLoading(false);
  };

  useEffect(() => {
    getOrganizationAsset(assetId);
    getOrganizationInvoicingAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateTicket = () => {
    const url = getTicketURI(organization?.locale.language);
    createTicket(url, asset);
  };

  const handleAssignToDifferentUserInitialization = async () => {
    const getOrganizationAllUsers = async () => {
      setOrganizationUsersLoading(true);
      const response = await getUsersFromOrganizationOrUserGroup('organization');
      if (response) {
        setOrganizationUsers(response);
        setAssignToDifferentUserModalOpen(true);
      } else {
        dispatch(showGrowlNotification(t('AssetCard.assign.errorFetchingOrganizationUsers'), 'error'));
      }
      setOrganizationUsersLoading(false);
    };
    // If user opens and closes the asset assign modal multiple times, get users from backend only on the first time
    if (organizationUsers.length === 0) {
      getOrganizationAllUsers();
    } else {
      setAssignToDifferentUserModalOpen(true);
    }
  };

  const handleTargetUserSelected = async (user: User): Promise<UserDetails> => {
    const response = await getAUser(user?.id || '', user?.user_group?.id || '');
    if (response) {
      return response;
    } else {
      // Reusable AssetAssignToUser component assumes this callback to throw if target user details fetching fails
      throw new Error('Fetching target user details failed');
    }
  };

  const handleAssignToDifferentUser = async (user: UserDetails, invoicingAccountId?: string): Promise<boolean> => {
    setAssetAssignLoading(true);
    let success;
    const assignedAsset = await assignAssetToUser(asset?.id || '', user.id, invoicingAccountId);
    if (assignedAsset) {
      dispatch(showGrowlNotification(t('AssetCard.assign.assetAssignSuccessfulUser', { targetUser: user.name || '' }), 'success'));
      setAssignToDifferentUserModalOpen(false);
      setAsset(assignedAsset);
      getUser(assignedAsset.end_user.user?.id || '');
      success = true;
    } else {
      success = false;
    }
    setAssetAssignLoading(false);
    if (!success) {
      getOrganizationAsset(assetId);
    }
    return success;
  };

  const handleAssignToOrganization = async (invoicingAccountId?: string): Promise<boolean> => {
    setAssetAssignLoading(true);
    let success = false;
    const assignedAsset = await assignAssetToOrganization(asset?.id || '', invoicingAccountId);
    if (assignedAsset) {
      dispatch(
        showGrowlNotification(t('AssetCard.assign.assetAssignSuccessfulOrganization', { targetOrganization: organization?.name || '' }), 'success'),
      );
      setAssignToOrganizationModalOpen(false);
      setAsset(assignedAsset);
      setAssetUser(undefined);
      success = true;
    }
    setAssetAssignLoading(false);
    if (!success) {
      getOrganizationAsset(assetId);
    }
    return success;
  };

  const getCurrentUserLink = (): string | undefined => {
    const currentUser = organizationUsers.find(orgUser => orgUser?.id && orgUser?.id === asset?.end_user.user?.id);
    if (currentUser?.id && currentUser?.user_group?.id) {
      return `/users/usergroup/${currentUser?.user_group?.id}/user/${currentUser?.id}`;
    }
    return undefined;
  };
  const currentUserLink = getCurrentUserLink();

  const handleUpdateLocationReference = async (newLocationReference: string) => {
    if (asset) {
      const updatedAsset = await updateOrganizationAsset(asset.id, {
        location_reference: newLocationReference || null,
        invoicing_account: asset.invoicing_account?.id,
      });
      if (updatedAsset) {
        setAsset(updatedAsset);
      } else {
        dispatch(showGrowlNotification(tGlobal('TopErrorBoundary.title'), 'error'));
      }
    }
  };

  const handleUpdateInvoicingAccount = async (invoicingAccount?: InvoicingAccountOptionResponseContents | InvoicingAccountResponse | undefined) => {
    if (asset) {
      const updatedAsset = await updateOrganizationAsset(asset.id, {
        location_reference: asset.location_reference,
        invoicing_account: invoicingAccount?.id || null,
      });
      if (updatedAsset) {
        setAsset(updatedAsset);
      } else {
        dispatch(showGrowlNotification(t('AssetCard.invoicingAccountSaveError'), 'error'));
      }
    }
  };

  const handleCreateDraftDecisionRequest = async () => {
    const res = await postEndOfLeaseDecision(assetId, 'admin', 'buyout', undefined, undefined, false);
    if (!res?.unconfirmed_end_of_lease_decisions?.length) {
      dispatch(showGrowlNotification(t('AssetCard.eolDecisionAskForBuyoutPricePostError'), 'error'));
      return undefined;
    }
    return res.unconfirmed_end_of_lease_decisions;
  };

  const handleConfirmDecisionRequest = async (request: AssetEndOfLeaseRequest) => {
    // Handle eol return decision
    if (request.return) {
      const assetResponse = await postEndOfLeaseDecision(assetId, 'admin', 'return', request.return.from, undefined, request.confirm);
      if (assetResponse) {
        return true;
      }
      dispatch(showGrowlNotification(t('AssetCard.eolDecisionPostError'), 'error'));
      return false;
    } else {
      // Handle other than return eol decision
      const res = await postEndOfLeaseDecision(
        assetId,
        'admin',
        request.buyout ? 'buyout' : 'extension',
        undefined,
        request.extension ? request.extension.length : undefined,
        request.confirm,
      );
      if (res) {
        return true;
      } else {
        dispatch(showGrowlNotification(t('AssetCard.eolDecisionPostError'), 'error'));
        return false;
      }
    }
  };

  const handleConfirmOrCancelDraftDecisionRequest = async (eolId: string, confirm: boolean) => {
    const res = await confirmOrCancelEndOfLeaseDecision(assetId, eolId, confirm, 'admin');
    if (!res && !confirm) {
      dispatch(showGrowlNotification(t('AssetCard.eolDecisionBuyoutCancelError'), 'error'));
    } else if (!res && confirm) {
      dispatch(showGrowlNotification(t('AssetCard.eolDecisionPostError'), 'error'));
    } else if (res && !confirm) {
      setAsset(res as OrganizationAssetResponse);
      dispatch(showGrowlNotification(t('AssetCard.endOfLeaseNotifications.priceRequestCancelled'), 'warning'));
    }
    return !!res;
  };

  const handleEndOfLeaseFlowFinish = (selectedChoice: EolFlowFinishFromChoice) => {
    dispatch(showGrowlNotification(t('AssetCard.eolDecisionMade'), 'success'));
    switch (selectedChoice) {
      case 'upgrade':
        if (asset) {
          dispatch(refreshUserInfo()).then(() => {
            const redirectPath = isWebshopEnabled(selectedOrganization, organization) ? '/webshop' : '/shop';
            if (!asset.end_user.user) {
              dispatch(shopSetOrderForOrganization());
              history.push(redirectPath);
            } else if (asset.end_user.user.id !== loggedInUser?.id) {
              getAUser(asset.end_user.user.id).then(endUser => {
                if (endUser) {
                  dispatch(shopSetOrderOnBehalfUser(endUser));
                }
                history.push(redirectPath);
              });
            } else {
              history.push(redirectPath);
            }
          });
        }
        break;
      case 'return':
        dispatch(refreshUserInfo());
        getOrganizationAsset(assetId);
        break;
      case 'extension':
        getOrganizationAsset(assetId);
        break;
      case 'buyout':
        dispatch(refreshUserInfo());
        getOrganizationAsset(assetId);
        break;
      default:
        getOrganizationAsset(assetId);
        break;
    }
  };

  const isAssignableToOrganization = (asset: OrganizationAssetResponse) => asset.assign_user_allowed && asset.end_user.user;

  const renderAssetCardOrNotFound = (asset: OrganizationAssetResponse | undefined, id: string) => {
    if (asset) {
      return (
        <>
          <OrganizationAssetCard
            companyName={selectedOrganization?.name || organization?.name || ''}
            asset={asset}
            userAssets={userAssets}
            isOpen={true}
            handleCreateTicket={handleCreateTicket}
            customSecondTicketModalContent={renderContactSupportText(t('AssetCard.secondTicketModal.content'))}
            translations={translations}
            currency={organization?.locale.currency || ''}
            endOfLeaseOptions={{
              translationsForEndOfLeaseChoice: endOfLeaseChoiceTranslations,
              handleCreateDraftDecisionRequest: handleCreateDraftDecisionRequest,
              handleConfirmDecisionRequest: handleConfirmDecisionRequest,
              handleConfirmOrCancelDraftDecisionRequest: handleConfirmOrCancelDraftDecisionRequest,
              handleEndOfLeaseFlowFinish: handleEndOfLeaseFlowFinish,
              buyoutWithPriceFlow: useBuyoutWithPriceFlow,
              showUpgrade: hasUserARequiredRole([UserRole.Orderer]),
              user: assetUser,
              // Allow return only for Norway addresses
              countries: [COUNTRIES.find(country => country.value === 'NO')!],
            }}
            handleAssignToDifferentUser={asset.assign_user_allowed ? handleAssignToDifferentUserInitialization : undefined}
            handleAssignToOrganization={isAssignableToOrganization(asset) ? () => setAssignToOrganizationModalOpen(true) : undefined}
            handleSaveLocationReference={handleUpdateLocationReference}
            fetchInvoicingAccounts={() => Promise.resolve(invoicingAccounts)}
            handleSaveInvoicingAccount={handleUpdateInvoicingAccount}
          />
          {organizationUsersLoading && <CustomLoader centered={true} />}
          {assetAssignLoading && <CustomLoader centered={true} dimBackground={true} />}
          {assignToDifferentUserModalOpen && (
            <AssetAssignToUser
              translations={assignToUserTranslations}
              assetToTransfer={asset}
              possibleTargetUsers={organizationUsers}
              onTargetUserSelect={handleTargetUserSelected}
              onSave={handleAssignToDifferentUser}
              onCancel={() => setAssignToDifferentUserModalOpen(false)}
              showConfirmation={true}
              invoicingAccounts={invoicingAccounts}
              getCurrentUserLink={currentUserLink ? () => currentUserLink : undefined}
              getTargetUserLink={user => `/users/usergroup/${user.user_group.id}/user/${user.id}`}
              currency={organization?.locale.currency || ''}
            />
          )}
          {organization && assignToOrganizationModalOpen && (
            <AssetAssignToOrganization
              translations={assignToOrganizationTranslations}
              asset={asset}
              targetOrganization={organization}
              onSave={handleAssignToOrganization}
              onCancel={() => setAssignToOrganizationModalOpen(false)}
              showConfirmation={true}
              invoicingAccounts={invoicingAccounts}
              getCurrentUserLink={currentUserLink ? () => currentUserLink : undefined}
              currency={organization?.locale.currency || ''}
            />
          )}
        </>
      );
    }
    return (
      <Title color={'black'} size={'md'}>
        {t('OrganizationAssets.assetNotFound')} {id}
      </Title>
    );
  };

  return (
    <>
      <Div
        style={{ display: 'inline-flex' }}
        margin={{ top: 'md', bottom: 'lg' }}
        onClick={() => history.push('/organizationassets')}
        clickableAppearance
      >
        <Icon margin={{ top: 'xxs', right: 'xs' }} color={'purple500'} size={'sm'} name={'chevron-left'}></Icon>
        <BodyText color={'purple500'}>{t('OrganizationAssets.goBackToDevices')}</BodyText>
      </Div>
      <Headline size={'lg'} margin={{ bottom: 'xlg' }}>
        {t('OrganizationAssets.deviceInformationTitle')}
      </Headline>
      {loading || invoicingAccountsLoading ? <CustomLoader /> : renderAssetCardOrNotFound(asset, assetId)}
    </>
  );
};

export default OrganizationAssetDetails;
